// component-assistant.ts — Assistant d'aide fonctionnelle (drawer latéral droit).
//
// UX validée : panneau qui glisse depuis la droite (~400px) et reste ouvert
// pendant la navigation. Le CONTENU des questions part directement au Worker
// air-gapped ; PMO-app ne fournit qu'un jeton court (via api.issueAssistantToken).
//
// i18n : libellés FR en dur pour la v1 (cf. modules i18n restants) ; vouvoiement
// respecté comme dans tout le contenu de l'app.

import type { AssistantConfig, AssistantToken } from '../api/assistant.js';

interface CachedToken {
  token: string;
  workerUrl: string;
  expMs: number;
}

const STYLE_ID = 'oa-assistant-style';
const EXAMPLES = [
  'Comment créer une action ?',
  'À quoi sert un comité ?',
  'Qu’est-ce qu’une révision ?',
];

let drawer: HTMLElement | null = null;
let bodyEl: HTMLElement | null = null;
let footEl: HTMLElement | null = null;
let inputEl: HTMLTextAreaElement | null = null;
let sendBtn: HTMLButtonElement | null = null;
let cachedToken: CachedToken | null = null;
let busy = false;

function injectStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = [
    '.oa-drawer{position:fixed;top:0;right:0;height:100vh;width:400px;max-width:100vw;background:#fff;',
    'box-shadow:-4px 0 24px rgba(0,0,0,.18);transform:translateX(100%);transition:transform .22s ease;',
    'z-index:1200;display:flex;flex-direction:column;font-size:14px}',
    '.oa-drawer.oa-open{transform:translateX(0)}',
    '.oa-head{display:flex;align-items:flex-start;gap:8px;padding:14px 16px;background:#147A57;color:#fff}',
    '.oa-title{flex:1;min-width:0}',
    '.oa-head h2{font-size:15px;margin:0;display:inline}',
    '.oa-ver{font-size:11px;opacity:.85}',
    '.oa-ai{display:block;font-size:11px;line-height:1.35;opacity:.85;margin-top:3px}',
    '.oa-close{background:none;border:none;color:#fff;font-size:22px;line-height:1;cursor:pointer}',
    '.oa-body{flex:1;overflow-y:auto;padding:14px 16px;display:flex;flex-direction:column;gap:12px}',
    '.oa-msg{max-width:85%;padding:9px 12px;border-radius:12px;line-height:1.45;white-space:pre-wrap;',
    'overflow-wrap:anywhere}',
    '.oa-user{align-self:flex-end;background:#147A57;color:#fff;border-bottom-right-radius:3px}',
    '.oa-bot{align-self:flex-start;background:#f1f3f5;color:#1f2937;border-bottom-left-radius:3px}',
    '.oa-foot{border-top:1px solid #e5e7eb;padding:10px 12px;display:flex;gap:8px}',
    '.oa-input{flex:1;resize:none;border:1px solid #cbd5e1;border-radius:8px;padding:8px 10px;font:inherit;',
    'max-height:90px}',
    '.oa-send{background:#147A57;color:#fff;border:none;border-radius:8px;padding:0 14px;cursor:pointer}',
    '.oa-send:disabled{opacity:.5;cursor:default}',
    '.oa-typing{display:inline-flex;gap:4px;align-items:center}',
    '.oa-dot{width:6px;height:6px;border-radius:50%;background:#9aa3af;animation:oa-blink 1.2s infinite}',
    '.oa-dot:nth-child(2){animation-delay:.2s}.oa-dot:nth-child(3){animation-delay:.4s}',
    '@keyframes oa-blink{0%,80%,100%{opacity:.3}40%{opacity:1}}',
    '.oa-ex{text-align:left;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:8px 10px;',
    'cursor:pointer;font:inherit;color:#147A57}',
    '.oa-note{color:#64748b;font-size:12.5px;line-height:1.5}',
    '.oa-thumbs{display:flex;gap:8px;margin-top:2px}',
    '.oa-thumb{background:none;border:none;cursor:pointer;font-size:14px;opacity:.55}',
    '.oa-thumb.oa-on{opacity:1}',
    '.oa-cta{align-self:flex-start;background:#147A57;color:#fff;border:none;border-radius:8px;',
    'padding:9px 12px;cursor:pointer;font:inherit}',
  ].join('');
  document.head.appendChild(style);
}

function build(): void {
  if (drawer) return;
  injectStyle();
  drawer = document.createElement('aside');
  drawer.className = 'oa-drawer';
  drawer.setAttribute('aria-label', 'Assistant Ornexia');
  drawer.setAttribute('role', 'complementary');

  const head = document.createElement('div');
  head.className = 'oa-head';
  // La mention IA vit dans l'ENTÊTE et pas dans l'écran d'accueil : elle doit
  // rester sous les yeux pendant toute la conversation, alors que l'accueil
  // disparaît dès la première question (cf. removeExamples).
  head.innerHTML =
    '<div class="oa-title"><h2>Assistant Ornexia</h2><span class="oa-ver" id="oa-ver"></span>' +
    '<span class="oa-ai">Robot conversationnel : c’est une intelligence ' +
    'artificielle qui vous répond, pas un membre de l’équipe.</span></div>' +
    '<button class="oa-close" aria-label="Fermer">&times;</button>';
  head.querySelector('.oa-close')?.addEventListener('click', close);

  bodyEl = document.createElement('div');
  bodyEl.className = 'oa-body';

  footEl = document.createElement('div');
  footEl.className = 'oa-foot';
  inputEl = document.createElement('textarea');
  inputEl.className = 'oa-input';
  inputEl.rows = 1;
  inputEl.placeholder = 'Posez votre question…';
  inputEl.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  });
  sendBtn = document.createElement('button');
  sendBtn.className = 'oa-send';
  sendBtn.textContent = 'Envoyer';
  sendBtn.addEventListener('click', () => void submit());
  footEl.appendChild(inputEl);
  footEl.appendChild(sendBtn);

  drawer.appendChild(head);
  drawer.appendChild(bodyEl);
  drawer.appendChild(footEl);
  document.body.appendChild(drawer);
}

function open(): void {
  build();
  drawer?.classList.add('oa-open');
}

function close(): void {
  drawer?.classList.remove('oa-open');
}

function clearBody(): void {
  if (bodyEl) bodyEl.innerHTML = '';
}

function setFootVisible(visible: boolean): void {
  if (footEl) footEl.style.display = visible ? 'flex' : 'none';
}

function note(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'oa-note';
  p.textContent = text;
  return p;
}

async function loadState(): Promise<void> {
  clearBody();
  setFootVisible(false);
  let config: AssistantConfig;
  try {
    config = await api.getAssistantConfig();
  } catch {
    bodyEl?.appendChild(note('L’assistant n’est pas disponible pour le moment.'));
    return;
  }
  const ver = document.getElementById('oa-ver');
  if (ver) ver.textContent = config.version ? `· ${config.version}` : '';

  if (!config.enabled) {
    bodyEl?.appendChild(note('L’assistant n’est pas disponible pour le moment.'));
    return;
  }
  if (!config.consented) {
    bodyEl?.appendChild(
      note(
        'Pour utiliser l’assistant, vous devez d’abord activer votre consentement ' +
          'au traitement par notre sous-traitant (transfert hors UE), dans « Mon compte ».',
      ),
    );
    const cta = document.createElement('button');
    cta.className = 'oa-cta';
    cta.textContent = 'Ouvrir « Mon compte »';
    cta.addEventListener('click', () => {
      close();
      toggleAccountMenu();
    });
    bodyEl?.appendChild(cta);
    return;
  }

  // État chat : exemples cliquables, puis saisie active.
  setFootVisible(true);
  renderExamples();
  inputEl?.focus();
}

function renderExamples(): void {
  if (!bodyEl) return;
  bodyEl.appendChild(note('Posez une question sur l’utilisation d’Ornexia, par exemple :'));
  for (const ex of EXAMPLES) {
    const b = document.createElement('button');
    b.className = 'oa-ex';
    b.textContent = ex;
    b.addEventListener('click', () => {
      if (inputEl) inputEl.value = ex;
      void submit();
    });
    bodyEl.appendChild(b);
  }
}

function removeExamples(): void {
  bodyEl?.querySelectorAll('.oa-ex, .oa-note').forEach((el) => el.remove());
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Rend le markdown minimal des réponses (gras **…**) en HTML ÉCHAPPÉ : on
// échappe d'abord tout le texte, puis on n'introduit que des <strong> contrôlés.
function renderBotHtml(text: string): string {
  return escapeHtml(text).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

function appendMessage(role: 'user' | 'bot', text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = `oa-msg ${role === 'user' ? 'oa-user' : 'oa-bot'}`;
  // Réponses du bot : markdown léger (gras) ; saisie utilisateur : texte brut.
  if (role === 'bot') el.innerHTML = renderBotHtml(text);
  else el.textContent = text;
  bodyEl?.appendChild(el);
  scrollDown();
  return el;
}

function appendTyping(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'oa-msg oa-bot oa-typing';
  el.innerHTML =
    '<span class="oa-dot"></span><span class="oa-dot"></span><span class="oa-dot"></span>';
  bodyEl?.appendChild(el);
  scrollDown();
  return el;
}

function appendThumbs(): void {
  const wrap = document.createElement('div');
  wrap.className = 'oa-thumbs';
  for (const [val, glyph] of [
    ['up', '👍'],
    ['down', '👎'],
  ] as const) {
    const b = document.createElement('button');
    b.className = 'oa-thumb';
    b.textContent = glyph;
    b.setAttribute('aria-label', val === 'up' ? 'Réponse utile' : 'Réponse peu utile');
    b.addEventListener('click', () => {
      // TODO analytics non-PII (version/section/verdict/vote) — endpoint à venir.
      wrap.querySelectorAll('.oa-thumb').forEach((tb) => tb.classList.remove('oa-on'));
      b.classList.add('oa-on');
    });
    wrap.appendChild(b);
  }
  bodyEl?.appendChild(wrap);
  scrollDown();
}

function scrollDown(): void {
  if (bodyEl) bodyEl.scrollTop = bodyEl.scrollHeight;
}

async function getToken(forceNew: boolean): Promise<CachedToken> {
  if (!forceNew && cachedToken && Date.now() < cachedToken.expMs) return cachedToken;
  const t: AssistantToken = await api.issueAssistantToken();
  cachedToken = {
    token: t.token,
    workerUrl: t.worker_url.replace(/\/+$/, ''),
    // Renouvelle 15 s avant l'expiration réelle.
    expMs: Date.now() + Math.max(0, t.ttl - 15) * 1000,
  };
  return cachedToken;
}

async function ask(question: string, allowRetry: boolean): Promise<string> {
  const tok = await getToken(false);
  const res = await fetch(`${tok.workerUrl}/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok.token}` },
    body: JSON.stringify({ question }),
  });
  if (res.status === 401 && allowRetry) {
    cachedToken = null;
    await getToken(true);
    return ask(question, false);
  }
  if (res.status === 429) {
    return 'Vous avez posé beaucoup de questions. Réessayez dans un moment.';
  }
  if (!res.ok) {
    return 'L’assistant est momentanément indisponible. Réessayez plus tard.';
  }
  const data = (await res.json()) as { answer?: string };
  return (data.answer || '').trim() || 'Je n’ai pas de réponse à vous proposer sur ce point.';
}

async function submit(): Promise<void> {
  if (busy || !inputEl) return;
  const question = inputEl.value.trim();
  if (!question) return;

  removeExamples();
  inputEl.value = '';
  appendMessage('user', question);

  busy = true;
  if (sendBtn) sendBtn.disabled = true;
  const typing = appendTyping();
  try {
    const answer = await ask(question, true);
    typing.remove();
    appendMessage('bot', answer);
    appendThumbs();
  } catch (e: unknown) {
    typing.remove();
    const msg = e instanceof Error ? e.message : 'Erreur inattendue';
    appendMessage('bot', msg);
  } finally {
    busy = false;
    if (sendBtn) sendBtn.disabled = false;
    inputEl?.focus();
  }
}

export function showAssistant(): void {
  // À la réouverture, on conserve la conversation en cours (le drawer reste dans
  // le DOM à la fermeture) : on ne réinitialise que s'il n'y a pas encore
  // d'échange (premier affichage, ou état non-consenti/indisponible).
  const hasConversation = !!bodyEl?.querySelector('.oa-msg');
  open();
  if (!hasConversation) void loadState();
}

declare global {
  interface Window {
    showAssistant?: () => void;
  }
}

window.showAssistant = showAssistant;
